/**
 * Theme Handler for White-Label Theming
 *
 * Listens for postMessage events from parent applications to apply
 * custom theme colors at runtime.
 *
 * Message format:
 * {
 *   type: 'SET_THEME',
 *   colors: {
 *     '--scoop-color-primary': '#0066CC',
 *     '--scoop-color-text-secondary': '#1A1A1A',
 *     // ... other CSS custom properties
 *   }
 * }
 */

/**
 * Initialize theme message listener
 * Call this once when the app starts
 */
export const initializeThemeHandler = () => {
    window.addEventListener('message', handleThemeMessage)
}

/**
 * Handle incoming theme messages from parent applications
 */
const handleThemeMessage = (event) => {
    // Parse message data
    const { type, colors, isDarkTheme } = event.data || {}

    // Only process SET_THEME messages
    if (type !== 'SET_THEME') {
        return
    }

    // Validate colors object
    if (!colors || typeof colors !== 'object') {
        return
    }

    // Store dark theme flag
    if (typeof isDarkTheme === 'boolean') {
        sessionStorage.setItem('scoop-is-dark-theme', isDarkTheme.toString())
    }

    // Apply theme colors
    applyTheme(colors)
}

/**
 * Apply theme colors to the document root
 * @param {Object} colors - Object with CSS custom property names as keys and color values as values
 */
export const applyTheme = (colors) => {
    const root = document.documentElement

    // Apply each color to the root element
    Object.entries(colors).forEach(([property, value]) => {
        // Validate property name (should start with --)
        if (!property.startsWith('--')) {
            return
        }

        // Validate value (basic check for non-empty string)
        if (!value || typeof value !== 'string') {
            return
        }

        // Apply the CSS custom property
        root.style.setProperty(property, value)
    })
}

/**
 * Remove theme message listener
 * Call this when unmounting or cleaning up
 */
export const cleanupThemeHandler = () => {
    window.removeEventListener('message', handleThemeMessage)
}

/**
 * Get current theme colors from the document
 * @returns {Object} Object with all scoop CSS custom properties
 */
export const getCurrentTheme = () => {
    const root = document.documentElement
    const computedStyle = getComputedStyle(root)
    const theme = {}

    // Get all scoop-prefixed CSS custom properties
    const scoopProperties = [
        '--scoop-color-primary',
        '--scoop-color-primary-hover',
        '--scoop-color-primary-light',
        '--scoop-color-primary-lighter',
        '--scoop-color-text-primary',
        '--scoop-color-text-secondary',
        '--scoop-color-text-tertiary',
        '--scoop-color-text-light',
        '--scoop-color-text-muted',
        '--scoop-color-text-inverse',
        '--scoop-bg-primary',
        '--scoop-bg-secondary',
        '--scoop-bg-tertiary',
        '--scoop-bg-light',
        '--scoop-bg-highlight',
        '--scoop-border-color',
        '--scoop-border-color-light',
        '--scoop-border-color-focus',
        '--scoop-border-color-hover',
        '--scoop-color-success',
        '--scoop-color-warning',
        '--scoop-color-error',
        '--scoop-color-info',
        '--scoop-color-link',
        '--scoop-color-link-hover',
    ]

    scoopProperties.forEach((property) => {
        const value = computedStyle.getPropertyValue(property).trim()
        if (value) {
            theme[property] = value
        }
    })

    return theme
}

/**
 * Get the current dark theme state
 * @returns {boolean} True if dark theme is active, false otherwise
 */
export const getIsDarkTheme = () => {
    const stored = sessionStorage.getItem('scoop-is-dark-theme')
    return stored === 'true'
}
